/**
 * Sentry Error Tracking Configuration
 * Initializes Sentry for error monitoring in production
 */
import * as Sentry from '@sentry/react';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

/**
 * Initialize Sentry error tracking
 * Should be called once at app startup (in main.tsx)
 */
export function initSentry(): void {
    if (!SENTRY_DSN) {
        console.warn('Sentry DSN not configured. Error tracking disabled.');
        return;
    }

    Sentry.init({
        dsn: SENTRY_DSN,
        environment: import.meta.env.MODE, // 'development' or 'production'

        // Only send errors in production
        enabled: import.meta.env.PROD,

        // Sample rate for error events (1.0 = 100%)
        sampleRate: 1.0,

        // Performance monitoring (optional, set to 0 to disable)
        tracesSampleRate: 0.1,

        // Filter out noisy errors
        ignoreErrors: [
            // Network errors
            'Network request failed',
            'Failed to fetch',
            // Browser extensions
            /^chrome-extension:\/\//,
            /^moz-extension:\/\//,
        ],

        // Add user context when available
        beforeSend(event) {
            // Remove sensitive data if needed
            return event;
        },
    });
}

/**
 * Capture an exception manually
 * Use this in catch blocks or error boundaries
 */
export function captureException(error: Error, context?: Record<string, unknown>): void {
    if (context) {
        Sentry.setContext('additional', context);
    }
    Sentry.captureException(error);
}

/**
 * Set user information for error context
 */
export function setUser(user: { id: string; email?: string; username?: string } | null): void {
    Sentry.setUser(user);
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(message: string, category: string = 'app'): void {
    Sentry.addBreadcrumb({
        message,
        category,
        level: 'info',
    });
}

export { Sentry };
